import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { siteConfig } from "@/config";

export const Route = createFileRoute("/affiliate-disclosure")({
  head: () => {
    const title = `Affiliate Disclosure — ${siteConfig.siteName}`;
    const desc = `How ${siteConfig.siteName} earns revenue, which links are affiliate links, and how those relationships do (and do not) influence our CRM reviews.`;
    const url = `${siteConfig.domain}/affiliate-disclosure`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: AffiliateDisclosure,
});

function AffiliateDisclosure() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-4xl font-bold tracking-tight">Affiliate Disclosure</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: January 2026
        </p>
        <div className="prose-article mt-8 space-y-6 text-base leading-relaxed">
          <p>
            {siteConfig.siteName} is reader-supported. When you click certain
            links on this site and go on to subscribe to a CRM product, we may
            receive a commission from the vendor at no additional cost to you.
            This page explains how that works and how it affects — and does not
            affect — what we publish.
          </p>

          <h2 className="text-2xl font-semibold">How we make money</h2>
          <p>
            Our revenue comes from two sources: (1) affiliate commissions paid
            by CRM vendors when a reader signs up for a paid plan after
            following one of our links, and (2) display advertising served by
            third parties such as Google AdSense. Advertisers do not receive
            editorial coverage in exchange for ad spend, and advertisers cannot
            purchase inclusion in any review, comparison, or "best of" list on
            this site.
          </p>

          <h2 className="text-2xl font-semibold">Which links are affiliate links</h2>
          <p>
            Outbound links to CRM vendors on category and review pages may be
            affiliate links. Links to primary sources — vendor documentation,
            pricing pages we cite for fact-checking, or news articles — are not
            monetized. We do not use affiliate links inside body text where a
            reader would reasonably expect a citation rather than a
            recommendation.
          </p>

          <h2 className="text-2xl font-semibold">How this affects our reviews</h2>
          <p>
            It does not. Products earn their spot on the strength of the pilot
            described in our{" "}
            <a href="/editorial-policy" className="underline">
              editorial policy
            </a>
            . We publish critical reviews of vendors we have affiliate
            relationships with, and we recommend products we do not earn any
            commission on when they are the right fit. Rankings are set by the
            editorial team before any commercial team reviews the draft.
          </p>

          <h2 className="text-2xl font-semibold">Legal basis</h2>
          <p>
            This disclosure is provided in accordance with the U.S. Federal
            Trade Commission's 16 CFR Part 255 guidelines on endorsements and
            testimonials, and equivalent consumer-protection rules in other
            jurisdictions. If you have questions about how a specific
            recommendation on this site was made, email{" "}
            <a
              href={`mailto:${siteConfig.contactEmail}`}
              className="underline"
            >
              {siteConfig.contactEmail}
            </a>
            .
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
